/**
 * Crisp chat: load widget only for allowlisted contact IDs.
 * Website ID is chosen per brand/website (see getWebsiteId):
 *   - theplaylistgeneration hosts -> VUE_APP_CRISP_WEBSITE_ID_TPG
 *   - everything else (GrayV)     -> VUE_APP_CRISP_WEBSITE_ID
 * (Vue env vars, inlined at build time.)
 */

const CRISP_ALLOWLIST = new Set([25441, 17034, 5861, 35900, 36050, 34271, 36053]);

let scriptInjected = false;

function normalizeEnvId(id) {
  if (id == null || String(id).trim() === '') {
    return null;
  }
  return String(id).trim();
}

// Same hostname convention as MainLayout's setBrand().
function isTpgHref(href) {
  return typeof href === 'string' && href.toLowerCase().includes('theplaylistgeneration');
}

/**
 * Pure resolver: pick the Crisp website id for a given URL + env.
 * theplaylistgeneration hosts use the TPG workspace (falling back to the
 * default GrayV id if it is unset); everything else uses the default.
 *
 * @param {string} href - current page URL
 * @param {Record<string, string|undefined>} env - process.env-like object
 * @returns {string|null}
 */
export function resolveCrispWebsiteId(href, env) {
  const source = env || {};
  if (isTpgHref(href)) {
    const tpgId = normalizeEnvId(source.VUE_APP_CRISP_WEBSITE_ID_TPG);
    if (tpgId) {
      return tpgId;
    }
  }
  return normalizeEnvId(source.VUE_APP_CRISP_WEBSITE_ID);
}

function getWebsiteId() {
  let href = '';
  try {
    href = (window.location && window.location.href) || '';
  } catch (e) {
    href = '';
  }
  return resolveCrispWebsiteId(href, process.env);
}

/**
 * @param {unknown} value - contact id, or user object from /user with contact_id or id
 * @returns {number|null}
 */
export function normalizeContactId(value) {
  if (value == null) {
    return null;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const raw = value.contact_id != null ? value.contact_id : value.id;
    return normalizeContactId(raw);
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {unknown} userOrContactId - contacts.id or /user payload
 */
export function isCrispAllowedForContact(userOrContactId) {
  const n = normalizeContactId(userOrContactId);
  if (n == null) {
    return false;
  }
  return CRISP_ALLOWLIST.has(n);
}

function crispPush(args) {
  window.$crisp = window.$crisp || [];
  window.$crisp.push(args);
}

function clearCrispTokenId() {
  try {
    delete window.CRISP_TOKEN_ID;
  } catch (e) {
    window.CRISP_TOKEN_ID = null;
  }
}

/**
 * @param {Record<string, unknown>} user - /user response body
 */
function applyCrispSessionTokenForUser(user) {
  const raw = user && user.crisp_session_token;
  if (raw != null && String(raw).trim() !== '') {
    window.CRISP_TOKEN_ID = String(raw).trim();
  } else {
    clearCrispTokenId();
  }
}

export function resetCrispSession() {
  clearCrispTokenId();
  if (!Array.isArray(window.$crisp)) {
    return;
  }
  try {
    window.$crisp.push(['do', 'session:reset']);
  } catch (e) {
    // ignore
  }
}

function hideCrispChat() {
  if (!Array.isArray(window.$crisp)) {
    return;
  }
  try {
    window.$crisp.push(['do', 'chat:hide']);
  } catch (e) {
    // ignore
  }
}

function injectCrispScript(websiteId) {
  window.$crisp = window.$crisp || [];
  window.CRISP_WEBSITE_ID = websiteId;
  if (scriptInjected) {
    return;
  }
  scriptInjected = true;
  const d = document;
  const s = d.createElement('script');
  s.src = 'https://client.crisp.chat/l.js';
  s.async = 1;
  const head = d.getElementsByTagName('head')[0];
  if (head) {
    head.appendChild(s);
  }
}

function identifyCrispUser(user, contactId) {
  const email = user.email != null ? String(user.email) : '';
  if (email) {
    crispPush(['set', 'user:email', [email]]);
  }
  const first = user.first_name != null ? String(user.first_name).trim() : '';
  const last = user.last_name != null ? String(user.last_name).trim() : '';
  const nickname = [first, last].filter(Boolean).join(' ').trim();
  if (nickname) {
    crispPush(['set', 'user:nickname', [nickname]]);
  }
  crispPush(['set', 'session:data', [[['contact_id', String(contactId)]]]]);
}

/**
 * Load Crisp and bind session for allowlisted users; reset/hide when not allowlisted
 * but Crisp was previously loaded in this tab.
 *
 * @param {Record<string, unknown>} user - /user response body
 */
export function initCrispForUser(user) {
  const websiteId = getWebsiteId();
  const contactId = normalizeContactId(user);
  const allowed = contactId != null && isCrispAllowedForContact(contactId);

  if (!allowed || !websiteId) {
    if (scriptInjected) {
      resetCrispSession();
      hideCrispChat();
    }
    return;
  }

  const wasInjected = scriptInjected;
  applyCrispSessionTokenForUser(user);
  injectCrispScript(websiteId);
  // session:reset after setting CRISP_TOKEN_ID so Crisp drops any prior local session
  // (cookies / storage) and rebinds to the token. Without this, a browser that already
  // had an anonymous or older Crisp session often ignores cross-device sync until
  // "clean" storage (e.g. private mode).
  const hasToken =
    user &&
    user.crisp_session_token != null &&
    String(user.crisp_session_token).trim() !== '';
  if (hasToken || wasInjected) {
    crispPush(['do', 'session:reset']);
  }
  identifyCrispUser(user, contactId);
}
